import { DirectiveNames, PageNames, RouteNames } from '@/acl/permissions';
import { ServicesBuilder } from '@/utils/setup';
import { EmployeeRole, EmployeeRoleService, IEmployeeRoleService } from '@posx/core';
import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { Subscription } from 'dexie';
import { logLiveQueryError, isLiveQueryCursorError } from '@/utils/logger';
import { getPlatform } from '@/utils/misc';

export const SYSTEM_ROLES = ['Cashier', 'Manager', 'Admin'];

// Role exclusion rules: permissions NOT granted to each role
const ROLE_EXCLUSIONS: Record<string, { directives?: DirectiveNames[]; pages?: PageNames[]; routes?: RouteNames[] }> = {
  Cashier: {
    directives: [DirectiveNames.FUNCTION_VOID, DirectiveNames.FUNCTION_VOID_AND_DUPLICATE, DirectiveNames.FUNCTION_CHANGE_PAYMENT, DirectiveNames.FUNCTION_FLAT_DISCOUNT, DirectiveNames.FUNCTION_PERCENT_DISCOUNT, DirectiveNames.FUNCTION_OPEN_DRAWER, DirectiveNames.FUNCTION_CRM_DELETE_CUSTOMER],
    pages: [PageNames.MANAGEMENT_EMPLOYEE_PAGE, PageNames.MANAGEMENT_SETUP_PAGE],
    routes: [RouteNames.SETTINGS, RouteNames.REPORTING, RouteNames.ANALYTICS],
  },
  Manager: {
    directives: [DirectiveNames.FUNCTION_CRM_DELETE_CUSTOMER],
    pages: [PageNames.MANAGEMENT_EMPLOYEE_PAGE],
    routes: [RouteNames.SETTINGS],
  },
};

// Calculate expected permissions for a role based on its exclusion rules
export function getExpectedPermissions(roleName: string): string[] {
  const ex = ROLE_EXCLUSIONS[roleName] ?? {};
  return [
    ...Object.values(RouteNames).filter(n => !ex.routes?.includes(n)),
    ...Object.values(DirectiveNames).filter(n => !ex.directives?.includes(n)),
    ...Object.values(PageNames).filter(n => !ex.pages?.includes(n)),
  ];
}

export const useEmployeeRoleStore = defineStore('role', {
  state: () => ({
    service: null as IEmployeeRoleService | null,
    list: [] as EmployeeRole[],
    _subscription: null as Subscription | null,
  }),
  actions: {
    async getService(): Promise<IEmployeeRoleService> {
      if (!this.service)
        // service singleton is method-only, skip deep proxy
        this.service = markRaw(await ServicesBuilder.getInstance().getService<IEmployeeRoleService>(EmployeeRoleService));
      return this.service;
    },
    async init() {
      const service = await this.getService();
      service.getAll();
      // Create default roles if not exist (permissions updated on login via acl.ts)
      if (!await service.getOneByParams({ name: 'Cashier' })) {
        const cashier = new EmployeeRole();
        cashier.name = 'Cashier';
        cashier.is_default = true;
        cashier.permissions = getExpectedPermissions('Cashier');
        await service.addOne(cashier);
      }
      if (!await service.getOneByParams({ name: 'Manager' })) {
        const manager = new EmployeeRole();
        manager.name = 'Manager';
        manager.permissions = getExpectedPermissions('Manager');
        await service.addOne(manager);
      }
      if (!await service.getOneByParams({ name: 'Admin' })) {
        const admin = new EmployeeRole();
        admin.name = 'Admin';
        admin.permissions = getExpectedPermissions('Admin');
        await service.addOne(admin);
      }
      // One-time migration: patch existing custom roles with new CRM add/edit permissions (not delete - admin grants manually)
      const customRoles = (await service.table.toArray()).filter(r => !SYSTEM_ROLES.includes(r.name) && !r.deleted_at);
      const newPerms = [DirectiveNames.FUNCTION_CRM_ADD_CUSTOMER, DirectiveNames.FUNCTION_CRM_EDIT_CUSTOMER];
      for (const role of customRoles) {
        const missing = newPerms.filter(p => !role.permissions.includes(p));
        if (missing.length > 0) {
          role.permissions = [...role.permissions, ...missing];
          await service.updateOne(role.id_in_server, role);
        }
      }
    },
    async startLiveQuery() {
      if (getPlatform() === 'mobile-web') return;
      if (this._subscription) return;
      const service = await this.getService();
      const sub: Subscription = service
        .liveQuery(() => service.table.filter((r: EmployeeRole) => r.deleted_at === undefined || r.deleted_at === null).toArray())
        .subscribe({
          next: async (resultPromise) => {
            const result = await resultPromise;
            this.list = result || [];
          },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'employee.role' }); if (isLiveQueryCursorError(err) && this._subscription === sub) { sub.unsubscribe(); this._subscription = null; } },
        });
      this._subscription = markRaw(sub);
    },
    stopLiveQuery() {
      this._subscription?.unsubscribe();
      this._subscription = null;
    },
    async getList() {
      const service = await this.getService();
      const result = await service.getMany({}, { pageSize: 100 });
      this.list = (result?.list || []).filter((r: EmployeeRole) => r.deleted_at === undefined || r.deleted_at === null);
      return this.list;
    },
  },
});
